import { Injectable, inject } from '@angular/core';

import { Fixture, FixtureStage, FixtureType, OfferCategory } from '@cds/betting-offer';
import { BatchRequest, FixtureRequest, FixtureState, OfferMapping, ScoreboardMode } from '@cds/query-objects';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { BatchResponse, BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { FavouritesService, isFixtureFavourite } from '@frontend/sports/favourites/core/feature';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { UserService } from '@frontend/sports/user/feature';
import { MediaQueryService } from '@frontend/vanilla/core';
import { entries, groupBy, sortBy } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';

import { FixtureList } from '../competition-list/competition.models';
import { LiveEventsSubscriptionService } from './live-events-subscription.service';

@Injectable({ providedIn: 'root' })
export class LiveFavouriteFixturesService {
    private userService = inject(UserService);
    private favouritesConfig = inject(FavouritesConfig);
    private bettingOfferApi = inject(BettingOfferApi);
    private favouriteService = inject(FavouritesService);
    private subscriptionsService = inject(LiveEventsSubscriptionService);
    private fixtureFactory = inject(FixtureFactory);
    private media = inject(MediaQueryService);
    private statisticsConfigService = inject(StatisticsConfigService);

    private fixtures$ = new BehaviorSubject<Fixture[]>([]);

    get change(): Observable<Fixture[]> {
        return this.fixtures$;
    }

    get fixtures(): Fixture[] {
        return this.fixtures$.getValue();
    }

    get liveFixtures(): Fixture[] {
        return this.fixtures.filter((fixture) => fixture.stage === FixtureStage.Live);
    }

    constructor() {
        const productUtils = inject(SportsProductUtils);

        const get = () => this.refresh(OfferMapping.None);

        this.userService.onUserLogin$.pipe(productUtils.filterSportsEmitLast()).subscribe(get);

        this.subscriptionsService.updatedEvents$.pipe(productUtils.filterSportsEmitLast()).subscribe((updatedEvents) => {
            const finishedFavourites = updatedEvents
                .filter((event) => event.scoreboard.isFinished)
                .map((event) =>
                    this.favouriteService.favouritesList.find((favourite) => isFixtureFavourite(favourite) && favourite.itemId === event.id),
                )
                .filter(isDefined);

            this.subscriptionsService.unsubscribe(finishedFavourites.map((f) => f.itemId));
            this.favouriteService.remove(finishedFavourites).subscribe();
        });

        get();
    }

    isFavourited(id: string): boolean {
        return this.liveFixtures.some((f) => f.id === id);
    }

    isSwipeFavouriteEnabled(): boolean {
        return this.favouritesConfig.isFavouritesEnabled && this.favouritesConfig.isFavouriteSwipeEnabled && this.media.isActive('lt-md');
    }

    isLiveFavouriteEnabled(): boolean {
        return this.favouritesConfig.isFavouritesEnabled && this.favouritesConfig.isLiveFavouritesEnabled && !this.media.isActive('lt-md');
    }

    refresh(offerMapping?: OfferMapping, offerCategories?: OfferCategory): void {
        const fixtureFavsIds = this.favouritedListToRefresh();

        if (!this.favouritesConfig.isLiveFavouritesEnabled || !fixtureFavsIds.length) {
            this.fixtures$.next([]);

            return;
        }

        this.getFixturesById(fixtureFavsIds, offerMapping, offerCategories).subscribe((batchFixtures: BatchResponse | undefined) => {
            if (!batchFixtures) {
                this.fixtures$.next([]);

                return;
            }

            const expiredFixtureIds = this.getExpiredFixtures(batchFixtures);
            const fixtureList = this.getFixtures(batchFixtures);

            this.removeExpired(expiredFixtureIds);
            this.subscribeToPush(fixtureList);

            this.fixtures$.next(fixtureList);
        });
    }

    getFixtureList(groupByCompetition: boolean = false): FixtureList[] {
        const groupedFixtures = groupBy(this.fixtures, (favourite) =>
            groupByCompetition ? favourite.competition.parentLeagueId || favourite.competition.id : favourite.sport.id,
        );

        const result = entries(groupedFixtures).map(([_, value]) => ({
            fixtures: value.sort((a, b) => (a.startDate > b.startDate ? 1 : -1)),
            totalCount: value.length,
        }));

        return sortBy(result, (current) => current.fixtures[0].sport.id);
    }

    updateFixtureList(): void {
        const current = this.favouritedList();
        const filtered = this.fixtures.filter((fixture) => current.some((id) => fixture.id === id));

        this.fixtures$.next(filtered);
    }

    private subscribeToPush(list: Fixture[]): void {
        list.filter((fixture) => !this.subscriptionsService.isSubscribed(fixture.id))
            .map((fixture) => this.fixtureFactory.create(fixture))
            .forEach((event) => this.subscriptionsService.subscribe(event));
    }

    private removeExpired(expiredFixtures: string[]): void {
        const expiredFavourites = this.favouriteService.favouritesList.filter((favourite) =>
            expiredFixtures.some((exId) => favourite.itemId === exId),
        );

        this.favouriteService.remove(expiredFavourites).subscribe();
    }

    private favouritedList(): string[] {
        return this.favouriteService.favouritesList.filter((f) => isFixtureFavourite(f) && f.count > 0).map(({ itemId }) => itemId);
    }

    private favouritedListToRefresh(): string[] {
        return this.favouriteService.favouritesList.filter(isFixtureFavourite).map(({ itemId }) => itemId);
    }

    private getExpiredFixtures(batchFixtures: BatchResponse): string[] {
        const expiredFixtures: string[] = [];

        entries(batchFixtures).forEach(([key, value]) => {
            if (!value.fixtures.length) {
                expiredFixtures.push(key);
            } else {
                const fixture = value.fixtures[0];

                if (fixture.scoreboard) {
                    const scoreboardResponse = fixture.scoreboard as ScoreboardSlim;
                    const isFinished = scoreboardResponse.periodId === 255;
                    if (isFinished) {
                        expiredFixtures.push(key);
                    }
                }
            }
        });

        return expiredFixtures;
    }

    private getFixtures(batchFixtures: BatchResponse): Fixture[] {
        return entries(batchFixtures)
            .filter(([_, value]) => value.fixtures.length)
            .map(([_, value]) => value.fixtures[0]);
    }

    private getFixturesById(
        fixtureIds: string[],
        offerMapping?: OfferMapping,
        offerCategories?: OfferCategory,
    ): Observable<BatchResponse | undefined> {
        const request: BatchRequest<FixtureRequest>[] = fixtureIds.map((fixtureId) => ({
            batchId: fixtureId,
            request: {
                fixtureIds: fixtureId,
                fixtureTypes: FixtureType.Standard,
                state: FixtureState.Latest,
                offerMapping,
                offerCategories,
                scoreboardMode: ScoreboardMode.Slim,
                excludeLocationFilter: true,
                statisticsModes: this.statisticsConfigService.getStatisticsModesBySportId(undefined),
            },
        }));

        return this.bettingOfferApi.getBatchFixtureList(request);
    }
}
